export const GEOLOCATION_ERROR_CODES = {
  INSECURE_CONTEXT: "insecure_context",
  PERMISSION_DENIED: "permission_denied",
  POSITION_UNAVAILABLE: "position_unavailable",
  TIMEOUT: "timeout",
  UNSUPPORTED: "unsupported",
  UNKNOWN: "unknown",
};

const DEFAULT_GEOLOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 300000,
};

const BASE_LOCATION_FAILURE_MESSAGE =
  "We couldn't access your current location. Please allow location access in your browser, or enter your city/county manually.";

const PERMISSION_HELP_TEXT = [
  "Check your browser location permission for MyFarmingPal.",
  "On some devices, location services must also be enabled in system settings.",
];

const isLocalDevelopmentHost = (hostname = "") =>
  ["localhost", "127.0.0.1", "::1", ""].includes(hostname);

const normalizeErrorCode = (error = {}) => {
  const rawCode = error?.code;

  if (rawCode === 1 || rawCode === "1" || rawCode === GEOLOCATION_ERROR_CODES.PERMISSION_DENIED) {
    return GEOLOCATION_ERROR_CODES.PERMISSION_DENIED;
  }

  if (rawCode === 2 || rawCode === "2" || rawCode === GEOLOCATION_ERROR_CODES.POSITION_UNAVAILABLE) {
    return GEOLOCATION_ERROR_CODES.POSITION_UNAVAILABLE;
  }

  if (rawCode === 3 || rawCode === "3" || rawCode === GEOLOCATION_ERROR_CODES.TIMEOUT) {
    return GEOLOCATION_ERROR_CODES.TIMEOUT;
  }

  if (rawCode === GEOLOCATION_ERROR_CODES.UNSUPPORTED) {
    return GEOLOCATION_ERROR_CODES.UNSUPPORTED;
  }

  if (rawCode === GEOLOCATION_ERROR_CODES.INSECURE_CONTEXT) {
    return GEOLOCATION_ERROR_CODES.INSECURE_CONTEXT;
  }

  return GEOLOCATION_ERROR_CODES.UNKNOWN;
};

export const getGeolocationSupportStatus = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return {
      ok: false,
      code: GEOLOCATION_ERROR_CODES.UNSUPPORTED,
      message:
        "Current-location access is not available in this browser. Please enter your city/county manually.",
    };
  }

  const hostname = window.location?.hostname || "";

  if (window.isSecureContext === false && !isLocalDevelopmentHost(hostname)) {
    return {
      ok: false,
      code: GEOLOCATION_ERROR_CODES.INSECURE_CONTEXT,
      message:
        "Current-location access needs a secure HTTPS connection. Please use MyFarmingPal over HTTPS, or enter your city/county manually.",
      helpText: "Location works on HTTPS production and localhost development.",
    };
  }

  if (!navigator.geolocation) {
    return {
      ok: false,
      code: GEOLOCATION_ERROR_CODES.UNSUPPORTED,
      message:
        "This browser does not support current-location access. Please enter your city/county manually.",
    };
  }

  return { ok: true, code: "supported" };
};

export const getGeolocationPermissionState = async () => {
  if (typeof navigator === "undefined" || !navigator.permissions?.query) {
    return "unknown";
  }

  try {
    const permission = await navigator.permissions.query({ name: "geolocation" });
    return permission?.state || "unknown";
  } catch (error) {
    return "unknown";
  }
};

export const getBrowserGeolocation = (options = {}) =>
  new Promise((resolve, reject) => {
    const support = getGeolocationSupportStatus();

    if (!support.ok) {
      reject({
        code: support.code,
        message: support.message,
        helpText: support.helpText,
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, {
      ...DEFAULT_GEOLOCATION_OPTIONS,
      ...options,
    });
  });

export const describeGeolocationError = (error = {}) => {
  const code = normalizeErrorCode(error);

  if (code === GEOLOCATION_ERROR_CODES.PERMISSION_DENIED) {
    return {
      code,
      message:
        "We couldn't access your current location because location permission is blocked. Please allow location access in your browser, or enter your city/county manually.",
      helpText: PERMISSION_HELP_TEXT,
    };
  }

  if (code === GEOLOCATION_ERROR_CODES.POSITION_UNAVAILABLE) {
    return {
      code,
      message:
        "Your device could not determine its current location. Please make sure location services are enabled, or enter your city/county manually.",
      helpText: PERMISSION_HELP_TEXT,
    };
  }

  if (code === GEOLOCATION_ERROR_CODES.TIMEOUT) {
    return {
      code,
      message:
        "We couldn't get your location before the request timed out. Please try again, move where your device has signal, or enter your city/county manually.",
      helpText: "You can still save a city/county manually.",
    };
  }

  if (code === GEOLOCATION_ERROR_CODES.UNSUPPORTED) {
    return {
      code,
      message:
        "This browser does not support current-location access. Please enter your city/county manually.",
    };
  }

  if (code === GEOLOCATION_ERROR_CODES.INSECURE_CONTEXT) {
    return {
      code,
      message:
        "Current-location access needs a secure HTTPS connection. Please use MyFarmingPal over HTTPS, or enter your city/county manually.",
      helpText: "Location works on HTTPS production and localhost development.",
    };
  }

  return {
    code,
    message: BASE_LOCATION_FAILURE_MESSAGE,
    helpText: error?.helpText || "",
  };
};

